import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/v1';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Selected cycle number — set by CycleContext, used by request interceptor
let _selectedCycleNumber = null;
api.setSelectedCycle = (cycleNumber) => { _selectedCycleNumber = cycleNumber; };
api.getSelectedCycle = () => _selectedCycleNumber;

// Request interceptor to add auth token and cycle parameter
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Auto-append cycle param to GET /journal requests
        if (_selectedCycleNumber && config.method === 'get' && config.url === '/journal') {
            config.params = config.params || {};
            if (!config.params.cycle) {
                config.params.cycle = _selectedCycleNumber;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Global lock for token refresh — prevents multiple concurrent refresh attempts
let _refreshPromise = null;

// Response interceptor to handle token refresh and role-based errors
api.interceptors.response.use(
    (response) => {
        // Dispatch event when journal entry is created so CycleContext can refresh
        if (response.config.method === 'post' && response.config.url === '/journal') {
            window.dispatchEvent(new CustomEvent('journal-entry-created'));
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized - Token expired or invalid
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // If a refresh is already in flight, wait for it instead of starting another
            if (!_refreshPromise) {
                const refreshToken = localStorage.getItem('refreshToken');
                if (!refreshToken) {
                    localStorage.removeItem('accessToken');
                    window.location.href = '/login';
                    return Promise.reject(error);
                }

                _refreshPromise = axios.post(`${API_URL}/auth/refresh`, { refreshToken }, { timeout: 10000 })
                    .then(response => {
                        const { accessToken } = response.data;
                        localStorage.setItem('accessToken', accessToken);
                        return accessToken;
                    })
                    .catch(refreshError => {
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        window.location.href = '/login';
                        throw refreshError;
                    })
                    .finally(() => {
                        _refreshPromise = null;
                    });
            }

            try {
                const accessToken = await _refreshPromise;
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        // Handle 403 Forbidden - Role-based access denied
        if (error.response?.status === 403) {
            console.error('Access denied - Insufficient privileges');
            // Don't redirect automatically, let the component handle this
            return Promise.reject(error);
        }

        // Handle 429 Rate Limit - Too many requests
        if (error.response?.status === 429) {
            const message = error.response?.data?.message || 'Too many requests, please try again later.';
            toast.error(message, {
                toastId: 'rate-limit-toast',
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default api;
